import { type ComponentProps, Fragment, type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../common/functions/cn';
import {
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxInput,
  ComboboxMultipleLabel,
  type ComboboxPresetBaseProps,
  ComboboxRoot,
  type ComboboxRootProps,
  ComboboxValue,
  useComboboxAnchor,
} from './combobox';
import {
  ComboboxInlineFrame,
  ComboboxPresetContent,
  ComboboxSelectionChip,
  type CreatableMarker,
  type CreatableProps,
  comboboxInlineInputStyles,
  isCreatable,
  type QueryProps,
  useComboboxInputValue,
  useCreatableHelpers,
  useCreatableItemEquality,
} from './combobox-internals';

export type MultiComboboxProps<T> = ComboboxPresetBaseProps<T> &
  Pick<ComboboxRootProps<T, true>, 'isItemEqualToValue' | 'itemToStringLabel' | 'disabled'> &
  Pick<ComponentProps<typeof ComboboxChipsInput>, 'placeholder'> &
  Omit<ComponentProps<typeof ComboboxChips>, 'value' | 'onValueChange' | 'children' | 'showClear'> &
  QueryProps &
  CreatableProps<T> & {
    /** The selected items. */
    value?: T[];
    /** Fired with the full new selection (an empty array when cleared). */
    onValueChange?: (value: T[]) => void;
    /** Whether a page is currently in-flight. Suppresses repeat `onLoadMore` fires while true. */
    isLoading?: boolean;
    /** Whether more pages remain. When false, the tail probe is not rendered. */
    hasMore?: boolean;
    /** Fired when the user scrolls near the bottom of the loaded set. Detected via an IntersectionObserver on a 1px probe element after the rows. */
    onLoadMore?: () => void;
    /** Extra content rendered in the popup below the list, outside the filtered collection (e.g. `ComboboxActions`). */
    children?: ReactNode;
    /** Blocks opening, typing, clearing and chip removal, without the disabled styling. */
    readOnly?: boolean;
    /**
     * Renders a clear affordance: in the control at the default size, on the collapsed summary chip at `size="sm"`,
     * where the control's one button slot has to stay with the chevron. Needs `collapseMultiple` to show at `sm`;
     * without it, individual chips carry their own remove buttons instead.
     */
    showClear?: boolean;
    /** Renders the option list in place under a search input instead of in a popup, with no chips. */
    inline?: boolean;
  };

type InFlightState<T> = { status: 'idle' } | { status: 'pending' | 'sticky'; value: T[] };

const IDLE: InFlightState<never> = { status: 'idle' };

export const MultiCombobox = <T,>({
  items,
  value,
  onValueChange,
  getKey,
  getLabel,
  getDescription,
  isItemEqualToValue,
  itemToStringLabel,
  placeholder,
  emptyMessage,
  disabled,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  query,
  onQueryChange,
  onCreate,
  renderCreateOption,
  id,
  collapseMultiple = false,
  children,
  readOnly,
  size = 'default',
  showClear = false,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  'aria-describedby': ariaDescribedBy,
  inline = false,
  ...chipsProps
}: MultiComboboxProps<T>) => {
  const { t } = useTranslation();
  const anchor = useComboboxAnchor();

  const clearOnChip = showClear && size === 'sm';

  const isServerFilter = query !== undefined && onQueryChange !== undefined;
  const { typedQuery, handleInputValueChange, clearQuery } = useComboboxInputValue(
    isServerFilter ? onQueryChange : undefined
  );
  const [inFlight, setInFlight] = useState<InFlightState<T>>(IDLE);

  const { itemsForView, localFilter, stableItemToStringLabel } = useCreatableHelpers(
    items,
    getLabel,
    itemToStringLabel,
    onCreate,
    typedQuery ?? ''
  );

  const defaultIsItemEqualToValue = useCreatableItemEquality(getKey);
  const effectiveIsItemEqualToValue = isItemEqualToValue ?? defaultIsItemEqualToValue;

  const handleValueChange = useCallback<NonNullable<ComboboxRootProps<T, true>['onValueChange']>>(
    async (next) => {
      const hasCreatable = next.some(isCreatable);

      if (hasCreatable && inFlight.status === 'sticky') {
        setInFlight({ status: 'sticky', value: next });
        onValueChange?.(next.filter((item) => !isCreatable(item)) as T[]);
        return;
      }

      if (hasCreatable && onCreate) {
        const creatable = next.find(isCreatable) as unknown as CreatableMarker;
        setInFlight({ status: 'pending', value: next });

        try {
          const created = await onCreate(creatable.__create);
          const cleaned = next.filter((item) => !isCreatable(item)) as T[];
          onValueChange?.([...cleaned, created]);
          setInFlight(IDLE);
          clearQuery();
        } catch {
          setInFlight({ status: 'sticky', value: next });
        }

        return;
      }

      setInFlight(IDLE);
      onValueChange?.(next);
    },
    [onCreate, onValueChange, clearQuery, inFlight.status]
  );

  const valueForRoot = inFlight.status === 'idle' ? (value ?? []) : inFlight.value;

  const handleClear = () => {
    setInFlight(IDLE);
    onValueChange?.([]);
  };

  const showLoadingCopy = isLoading && !items?.length;
  const Frame = inline ? ComboboxInlineFrame : Fragment;

  return (
    <ComboboxRoot
      multiple
      items={itemsForView}
      value={valueForRoot}
      onValueChange={handleValueChange}
      disabled={disabled}
      readOnly={readOnly}
      inline={inline}
      open={inline || undefined}
      // Inline, nothing is highlighted until the user arrows in, so Enter still submits the surrounding form.
      autoHighlight={!inline}
      loopFocus={!hasMore}
      onOpenChange={(open, details) => {
        // Base UI closes after a pick while the input holds a query, so picking several matches means retyping it.
        if (!open && details.reason === 'item-press') {
          details.cancel();
        }
      }}
      itemToStringLabel={stableItemToStringLabel}
      isItemEqualToValue={effectiveIsItemEqualToValue}
      inputValue={typedQuery ?? ''}
      onInputValueChange={(next, details) => {
        // Inline, Base UI empties the search after every pick. Keep it so several matches can be ticked in one search.
        if (inline && details.reason === 'input-clear' && details.event?.type !== 'input') {
          return;
        }
        if (inFlight.status !== 'idle') {
          setInFlight(IDLE);
        }
        handleInputValueChange(next, details.reason);
      }}
      filter={isServerFilter ? null : localFilter}
    >
      <Frame>
        {inline ? (
          <ComboboxInput
            id={id}
            icon={chipsProps.icon}
            className={cn(comboboxInlineInputStyles, chipsProps.className)}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            size={size}
            showTrigger={false}
            aria-invalid={ariaInvalid}
            aria-required={ariaRequired}
            aria-describedby={ariaDescribedBy}
          >
            {valueForRoot.length > 0 && (
              <ComboboxSelectionChip
                title={valueForRoot.map((item) => (isCreatable(item) ? item.__create : getLabel(item))).join(', ')}
                onClear={disabled || readOnly ? undefined : handleClear}
              >
                {t(($) => $.comboboxSelectedCount, { count: valueForRoot.length })}
              </ComboboxSelectionChip>
            )}
          </ComboboxInput>
        ) : (
          <ComboboxChips
            ref={anchor}
            collapseMultiple={collapseMultiple}
            disabled={disabled}
            readOnly={readOnly}
            size={size}
            showClear={showClear && !clearOnChip}
            {...chipsProps}
          >
            <ComboboxValue>
              {collapseMultiple && valueForRoot.length > 1 ? (
                <ComboboxMultipleLabel
                  selectedItems={valueForRoot.filter((item) => !isCreatable(item))}
                  itemToString={getLabel}
                  showClear={clearOnChip}
                />
              ) : (
                valueForRoot.map((item) => {
                  if (isCreatable(item)) {
                    return <ComboboxChip key={`__create__${item.__create}`}>{item.__create}</ComboboxChip>;
                  }
                  return <ComboboxChip key={getKey(item)}>{getLabel(item)}</ComboboxChip>;
                })
              )}
            </ComboboxValue>
            <ComboboxChipsInput
              id={id}
              placeholder={placeholder}
              disabled={disabled}
              aria-invalid={ariaInvalid}
              aria-required={ariaRequired}
              aria-describedby={ariaDescribedBy}
            />
          </ComboboxChips>
        )}
        <ComboboxPresetContent
          anchor={inline ? undefined : anchor}
          items={itemsForView}
          getKey={getKey}
          getLabel={getLabel}
          getDescription={getDescription}
          isLoading={isLoading}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          renderCreateOption={renderCreateOption}
          isServerFilter={isServerFilter}
          emptyMessage={emptyMessage}
          showLoadingCopy={showLoadingCopy}
          inline={inline}
        >
          {children}
        </ComboboxPresetContent>
      </Frame>
    </ComboboxRoot>
  );
};
